import { useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import type { Player, PlayerPosition } from '../game'
import { CAREER_STAGES, COUNTRY_FLAGS, PLAYABLE_COUNTRIES, generatePlayerName, getRandomStartingClubs, isReserveClub, type ClubDefinition } from '../game'
import { ClubCrest } from './ClubCrest'

interface SetupScreenProps {
  onStart: (player: Player, club: string) => void
}

interface CountrySelectProps {
  value: string
  onChange: (country: string) => void
}

function CountrySelect({ value, onChange }: CountrySelectProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([])

  useEffect(() => {
    if (!open) return
    const selectedIndex = Math.max(0, PLAYABLE_COUNTRIES.indexOf(value as typeof PLAYABLE_COUNTRIES[number]))
    const frame = requestAnimationFrame(() => optionRefs.current[selectedIndex]?.focus())
    return () => cancelAnimationFrame(frame)
  }, [open, value])

  const choose = (nextCountry: string) => {
    onChange(nextCountry)
    setOpen(false)
    requestAnimationFrame(() => triggerRef.current?.focus())
  }

  const moveFocus = (currentIndex: number, offset: number) => {
    const nextIndex = (currentIndex + offset + PLAYABLE_COUNTRIES.length) % PLAYABLE_COUNTRIES.length
    optionRefs.current[nextIndex]?.focus()
  }

  return (
    <div ref={rootRef} className={`country-select${open ? ' is-open' : ''}`} onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false)
    }}>
      <button
        ref={triggerRef}
        className="country-select__trigger"
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            setOpen(true)
          }
        }}
      >
        <img src={COUNTRY_FLAGS[value]} alt="" />
        <span>{value}</span>
        <Icon icon="mdi:chevron-down" aria-hidden="true" />
      </button>
      {open && <div className="country-select__options" role="listbox" aria-label="País del jugador">
        {PLAYABLE_COUNTRIES.map((item, index) => <button
          ref={(node) => { optionRefs.current[index] = node }}
          key={item}
          type="button"
          role="option"
          aria-selected={item === value}
          tabIndex={item === value ? 0 : -1}
          onClick={() => choose(item)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') { event.preventDefault(); moveFocus(index, 1) }
            else if (event.key === 'ArrowUp') { event.preventDefault(); moveFocus(index, -1) }
            else if (event.key === 'Home') { event.preventDefault(); optionRefs.current[0]?.focus() }
            else if (event.key === 'End') { event.preventDefault(); optionRefs.current.at(-1)?.focus() }
            else if (event.key === 'Escape') { event.preventDefault(); setOpen(false); triggerRef.current?.focus() }
          }}
        >
          <img src={COUNTRY_FLAGS[item]} alt="" />
          <span>{item}</span>
          {item === value && <Icon icon="mdi:check" aria-hidden="true" />}
        </button>)}
      </div>}
    </div>
  )
}

interface ShirtNumberInputProps {
  value: string
  onChange: (number: string) => void
}

function ShirtNumberInput({ value, onChange }: ShirtNumberInputProps) {
  const numericValue = Number.parseInt(value, 10)
  const updateBy = (amount: number) => {
    const current = Number.isFinite(numericValue) ? numericValue : 1
    onChange(String(Math.max(1, Math.min(99, current + amount))))
  }

  return (
    <span className="number-stepper">
      <input
        required
        aria-label="Dorsal"
        type="number"
        inputMode="numeric"
        min="1"
        max="99"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <span className="number-stepper__controls">
        <button type="button" aria-label="Restar uno al dorsal" disabled={numericValue <= 1} onClick={() => updateBy(-1)}><Icon icon="mdi:minus" /></button>
        <button type="button" aria-label="Sumar uno al dorsal" disabled={numericValue >= 99} onClick={() => updateBy(1)}><Icon icon="mdi:plus" /></button>
      </span>
    </span>
  )
}

export function SetupScreen({ onStart }: SetupScreenProps) {
  const [name, setName] = useState('')
  const [nameError, setNameError] = useState('')
  const [number, setNumber] = useState('10')
  const [country, setCountry] = useState('España')
  const [position, setPosition] = useState<PlayerPosition>('MCO')
  const [offers, setOffers] = useState<readonly ClubDefinition[] | null>(null)
  const dialogRef = useRef<HTMLElement>(null)
  const submitRef = useRef<HTMLButtonElement>(null)
  const nameRef = useRef<HTMLInputElement>(null)

  const player = (): Player => ({ name: name.trim(), number, country, position })
  const closeOffers = () => setOffers(null)
  const generateName = () => {
    setName(generatePlayerName(country))
    setNameError('')
  }

  useEffect(() => {
    if (!offers) return
    const previousOverflow = document.body.style.overflow
    const previousRootOverflow = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    const focusFrame = requestAnimationFrame(() => {
      if (!dialogRef.current) return
      dialogRef.current.scrollTop = 0
      dialogRef.current.focus({ preventScroll: true })
    })
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        closeOffers()
        return
      }
      if (event.key !== 'Tab' || !dialogRef.current) return
      const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>('button:not(:disabled)')]
      const first = focusable[0]
      const last = focusable.at(-1)
      if (!first || !last) return
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      cancelAnimationFrame(focusFrame)
      document.body.style.overflow = previousOverflow
      document.documentElement.style.overflow = previousRootOverflow
      document.removeEventListener('keydown', onKeyDown)
      submitRef.current?.focus()
    }
  }, [offers])

  return (
    <section className="setup-layout" aria-labelledby="setup-title">
      <div className="setup-story" aria-hidden={offers ? true : undefined}>
        <p className="eyebrow">Desde la cantera · Etapa 01</p>
        <h1 id="setup-title">Una carrera.<br /><em>Cinco etapas.</em></h1>
        <p className="setup-story__intro">Empieza en la cantera de un club de tu país. Después podrás renovar o llevar tu carrera a otras ligas.</p>
        <ol className="career-stages" aria-label="Las cinco etapas de la carrera">
          {CAREER_STAGES.map((stage, index) => <li key={stage.id}><span>{String(index + 1).padStart(2, '0')}</span><div><strong>{stage.name}</strong><small>{stage.description}</small></div></li>)}
        </ol>
        <div className="setup-story__pitch" aria-hidden="true">
          <span className="pitch-player pitch-player--one">10</span>
          <span className="pitch-player pitch-player--two">9</span>
          <Icon icon="mdi:soccer" />
        </div>
        <p className="setup-story__note">Cada decisión escribe tu historia</p>
      </div>

      <form className="registration-card" aria-hidden={offers ? true : undefined} onSubmit={(event) => {
        event.preventDefault()
        if (name.trim().length < 2) {
          setNameError(name.trim() ? 'El nombre debe tener al menos 2 caracteres.' : 'Escribe el nombre de tu jugador.')
          nameRef.current?.focus()
          return
        }
        setNameError('')
        setOffers(getRandomStartingClubs(country))
      }}>
        <span className="registration-card__folio">Ficha 001</span>
        <p className="eyebrow">Ficha del jugador</p>
        <h2>Empieza en la cantera</h2>
        <p>Los datos se guardan solo en este navegador.</p>
        <div className="field-label player-name-field">
          <label htmlFor="player-name">Nombre</label>
          <span className="player-name-input">
            <input
            id="player-name"
            ref={nameRef}
            required
            minLength={2}
            autoComplete="name"
            aria-invalid={nameError ? true : undefined}
            aria-describedby={nameError ? 'player-name-error' : undefined}
            value={name}
            onChange={(event) => {
              setName(event.target.value)
              if (nameError && event.target.value.trim().length >= 2) setNameError('')
            }}
            onBlur={() => {
              if (name.trim().length === 0) setNameError('Escribe el nombre de tu jugador.')
            }}
            />
            <button type="button" onClick={generateName} aria-label={`Generar un nombre aleatorio de ${country}`} title={`Generar nombre de ${country}`}><Icon icon="mdi:dice-multiple-outline" /><span>Aleatorio</span></button>
          </span>
          {nameError && <small id="player-name-error" className="field-error" role="alert">{nameError}</small>}
        </div>
        <div className="form-row">
          <label>Dorsal<ShirtNumberInput value={number} onChange={setNumber} /></label>
          <label>País<CountrySelect value={country} onChange={setCountry} /></label>
        </div>
        <fieldset className="position-picker">
          <legend>Posición inicial</legend>
          {(['MCO', 'DC'] as const).map((item) => (
            <button className={position === item ? 'is-active' : ''} key={item} type="button" onClick={() => setPosition(item)}>
              <strong>{item}</strong><span>{item === 'MCO' ? 'Creador y asistente' : 'Finalizador y goleador'}</span>
            </button>
          ))}
        </fieldset>
        <button ref={submitRef} className="button button--primary" type="submit">Firmar ficha y ver ofertas <Icon icon="mdi:arrow-right" /></button>
      </form>

      {offers && <div className="dialog-backdrop club-offer-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeOffers() }}>
        <section ref={dialogRef} className="club-offer-dialog" role="dialog" aria-modal="true" aria-labelledby="club-offer-title" aria-describedby="club-offer-description" tabIndex={-1}>
          <button className="club-offer-dialog__close" type="button" onClick={closeOffers} aria-label="Volver a editar la ficha"><Icon icon="mdi:close" /></button>
          <header>
            <p className="eyebrow">Tres propuestas · Etapa 01</p>
            <h2 id="club-offer-title">Elige tu cantera</h2>
            <p id="club-offer-description">La progresión inicial es idéntica en las tres ofertas: cambian el club, la división y si firmas por un filial.</p>
          </header>
          <div className="club-offer-grid">
            {offers.map((club) => <button type="button" key={club.name} onClick={() => onStart(player(), club.name)}>
              <span className="club-offer-card__division">{club.division === 1 ? '1.ª división' : '2.ª división'}</span>
              <ClubCrest name={club.name} size="large" />
              <span className="club-offer-card__copy"><strong title={club.name}>{club.name}</strong><small title={club.league}>{club.league}</small><span className="club-offer-card__impact">{isReserveClub(club.name) ? 'Filial · renovar te asciende al primer equipo en la segunda temporada.' : `Cantera de ${club.division === 1 ? 'Primera' : 'Segunda'} · sin bonus estadístico inicial.`}</span></span>
              <span className="club-offer-card__action">Firmar por este club <Icon icon="mdi:arrow-right" /></span>
            </button>)}
          </div>
          <button className="text-button club-offer-dialog__back" type="button" onClick={closeOffers}>Volver y cambiar mis datos</button>
        </section>
      </div>}
    </section>
  )
}
